// ABOUTME: Renders the configurator drawer for the current step of a configuration chain.
// ABOUTME: Adds the two-step breadcrumb and the Back/Next/Save actions in the configurator header.

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@ds/coolshop';
import { trackAction } from '@src/_new/modules/user-analytics';
import { useIntl } from 'react-intl';

import { useChainedConfiguratorFlow } from '../../hooks/useChainedConfiguratorFlow';
import { useCpqSettings } from '../../hooks/useCpqSettings';
import { ConfiguratorDrawer } from './ConfiguratorDrawer';

interface ChainedConfiguratorFlowProps {
    flow: ReturnType<typeof useChainedConfiguratorFlow>;
    /**
     * The offer these configurations belong to. Sent with each request so the server can stamp its
     * metadata on the CAD drawing; absent when the flow is opened outside an offer.
     */
    offerId?: string;
    /** Shows the favorite star for the step's prototype in the drawer header. */
    enableFavoriteAction?: boolean;
}

export function ChainedConfiguratorFlow({
    flow,
    offerId,
    enableFavoriteAction,
}: ChainedConfiguratorFlowProps) {
    const { formatMessage } = useIntl();
    const cpqSettings = useCpqSettings();
    const chainingEnabled = Boolean(cpqSettings?.chainedConfiguration?.enable);

    const { session } = flow;
    const step = session?.steps[session.currentStep];
    if (!session || !step) return null;

    const isView = session.kind === 'view';
    const analyticsProps = { offerId: offerId ?? null, lineItemId: session.lineItemId ?? null };

    // Tenants without chaining get exactly the drawer they had before: no stepper, and the
    // configurator package's own finalize button as the single primary action.
    if (!chainingEnabled) {
        return (
            <ConfiguratorDrawer
                open
                mode={isView ? 'view' : step.mode}
                title={session.title}
                prototypeId={step.prototypeId}
                showFavoriteAction={enableFavoriteAction}
                initialUserAssignments={step.userAssignments}
                initialViewId={step.viewId}
                onClose={flow.close}
                onFinalize={(...args: Parameters<typeof flow.handleFinalize>) => {
                    trackAction('cpq.finalize-configuration', analyticsProps);
                    return flow.handleFinalize(...args);
                }}
                offerId={offerId}
                lineItemId={session.lineItemId}
            />
        );
    }

    // The second step is only part of the chain once it has been resolved or was already
    // persisted, so a first configuration that chains nowhere shows a single step.
    const stepLabels = [
        formatMessage({ id: 'cpq.configurator.step-one', defaultMessage: 'Product' }),
        ...(session.steps.length > 1
            ? [
                  formatMessage({
                      id: 'cpq.configurator.step-two',
                      defaultMessage: 'Linked product',
                  }),
              ]
            : []),
    ];

    const backLabel = formatMessage({ id: 'cpq.configurator.back', defaultMessage: 'Back' });
    const nextLabel = formatMessage({ id: 'cpq.configurator.next', defaultMessage: 'Next' });

    // These render inside the configurator's ActionHeader, an AppBar on the primary colour, so each
    // one carries the design system's contrast API: text buttons inverted, and the primary action in
    // the contained secondary style the package's own finalize button uses.
    const headerActions = isView
        ? () => (
              <>
                  {session.currentStep > 0 && (
                      <Button
                          variant="text"
                          color="primary"
                          invertedColor
                          contrastColor="primary"
                          onClick={() => flow.goToStep(session.currentStep - 1)}
                      >
                          {backLabel}
                      </Button>
                  )}
                  {session.currentStep < session.steps.length - 1 && (
                      <Button
                          variant="text"
                          color="primary"
                          invertedColor
                          contrastColor="primary"
                          onClick={() => flow.goToStep(session.currentStep + 1)}
                      >
                          {nextLabel}
                      </Button>
                  )}
              </>
          )
        : // Save line item, Next and Finalize & save each finalize the current step, so they are
          // gated on a complete configuration; Back only pages back and stays enabled.
          (requestFinalize: () => void, isComplete: boolean) =>
              session.currentStep === 0 ? (
                  <>
                      <Button
                          variant="text"
                          color="primary"
                          invertedColor
                          contrastColor="primary"
                          disabled={!isComplete}
                          onClick={() => flow.request('next', requestFinalize)}
                      >
                          {nextLabel}
                      </Button>
                      {/* Stops the chain here and keeps only this configuration on the line: the
                          primary action, last, as the finalize of every other step is. */}
                      <Button
                          variant="contained"
                          color="secondary"
                          contrastColor="primary"
                          disabled={!isComplete}
                          onClick={() => flow.request('save', requestFinalize)}
                          data-analytics="cpq.finalize-configuration"
                          data-analytics-props={JSON.stringify(analyticsProps)}
                      >
                          {formatMessage({
                              id: 'cpq.configurator.save-line-item',
                              defaultMessage: 'Save line item',
                          })}
                      </Button>
                  </>
              ) : (
                  <>
                      <Button
                          variant="text"
                          color="primary"
                          invertedColor
                          contrastColor="primary"
                          onClick={() => flow.request('back', requestFinalize)}
                      >
                          {backLabel}
                      </Button>
                      <Button
                          variant="contained"
                          color="secondary"
                          contrastColor="primary"
                          disabled={!isComplete}
                          onClick={() => flow.request('save', requestFinalize)}
                          data-analytics="cpq.finalize-configuration"
                          data-analytics-props={JSON.stringify(analyticsProps)}
                      >
                          {formatMessage({
                              id: 'cpq.configurator.finalize-and-save',
                              defaultMessage: 'Finalize & save',
                          })}
                      </Button>
                  </>
              );

    return (
        <>
            {/* Keyed on the step so the configurator remounts on the new product instead of
                keeping the previous step's resolved state. */}
            <ConfiguratorDrawer
                key={`${session.currentStep}-${step.prototypeId}`}
                open
                mode={isView ? 'view' : step.mode}
                title={session.title}
                prototypeId={step.prototypeId}
                showFavoriteAction={enableFavoriteAction}
                initialUserAssignments={step.userAssignments}
                initialViewId={step.viewId}
                onClose={flow.close}
                onFinalize={flow.handleFinalize}
                offerId={offerId}
                lineItemId={session.lineItemId}
                breadcrumbSteps={stepLabels}
                activeStepIndex={session.currentStep}
                headerActions={headerActions}
            />

            <Dialog open={Boolean(flow.pendingDiscard)} onClose={flow.cancelDiscard} maxWidth="sm">
                <DialogTitle>
                    {/* component="span": DialogTitle already renders an h2, and nesting an h4
                        inside it is invalid HTML. */}
                    <Typography variant="h4" component="span">
                        {formatMessage({
                            id: 'cpq.configurator.discard-step-two-title',
                            defaultMessage: 'Discard the linked configuration?',
                        })}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {formatMessage({
                            id: 'cpq.configurator.discard-step-two-confirm',
                            defaultMessage:
                                'The linked product configuration saved on this line item will be discarded. Continue?',
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={flow.cancelDiscard}>
                        {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button variant="contained" onClick={flow.confirmDiscard}>
                        {formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
